import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, ListTodo } from 'lucide-react';
import { format, parse, isValid } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { taskService, Task } from '@/services/taskService';
import TaskPatientSelect from './TaskPatientSelect';
import { DatePicker } from '@/components/ui/date-picker';

// Validation schema
const taskFormSchema = z.object({
  title: z.string().min(1, 'Title is required').max(500, 'Title must be 500 characters or less'),
  patient_id: z.string().uuid('Patient is required'),
  due_date: z
    .string()
    .min(1, 'Due date is required')
    .regex(/^\d{2}\/\d{2}\/\d{4}$/, 'Due date must be in MM/DD/YYYY format'),
  priority: z.enum(['high', 'medium', 'low']),
  category: z.enum(['follow_up_visit', 'lab_review', 'medication_check', 'patient_checkin', 'documentation', 'other']),
  notes: z.string().optional(),
});

type TaskFormData = z.infer<typeof taskFormSchema>;

const toDisplayDate = (dateValue?: string) => {
  if (!dateValue) return '';
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  if (isoMatch) {
    return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
  }
  return dateValue;
};

const toApiDate = (dateValue: string) => {
  const usMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateValue);
  if (usMatch) {
    return `${usMatch[3]}-${usMatch[1]}-${usMatch[2]}`;
  }
  return dateValue;
};

const toDateObject = (dateValue?: string) => {
  if (!dateValue) return undefined;

  const parsedUs = parse(dateValue, 'MM/dd/yyyy', new Date());
  if (isValid(parsedUs)) return parsedUs;

  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  if (isoMatch) {
    const parsedIso = parse(dateValue, 'yyyy-MM-dd', new Date());
    if (isValid(parsedIso)) return parsedIso;
  }

  return undefined;
};

interface TaskFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: string;
  editingTask?: Task | null;
  patientId?: string;
  encounterId?: string;
  onSuccess?: () => void;
}

export const TaskFormDialog: React.FC<TaskFormDialogProps> = ({
  open,
  onOpenChange,
  providerId,
  editingTask,
  patientId,
  encounterId,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<TaskFormData>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: '',
      patient_id: patientId || '',
      due_date: '',
      priority: 'medium',
      category: 'follow_up_visit',
      notes: '',
    },
  });

  // Load editing task data
  useEffect(() => {
    if (editingTask) {
      setValue('title', editingTask.title);
      setValue('patient_id', editingTask.patient_id);
      setValue('due_date', toDisplayDate(editingTask.due_date));
      setValue('priority', editingTask.priority);
      setValue('category', editingTask.category);
      setValue('notes', editingTask.notes || '');
    } else if (patientId) {
      setValue('patient_id', patientId);
    }
  }, [editingTask, patientId, setValue]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = async (data: TaskFormData) => {
    setIsLoading(true);

    try {
      const taskData = {
        ...data,
        due_date: toApiDate(data.due_date),
        assigned_to: providerId,
        encounter_id: encounterId,
        source: 'manual' as const,
      };

      if (editingTask) {
        await taskService.updateTask(editingTask.task_id, taskData);
        toast({
          title: 'Task Updated',
          description: 'Task has been updated successfully',
          variant: 'success',
        });
      } else {
        await taskService.createTask(taskData);
        toast({
          title: 'Task Created',
          description: 'New task has been created',
          variant: 'success',
        });
      }

      reset();
      onOpenChange(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error saving task:', err);
      toast({
        title: 'Error',
        description: err?.message || 'Failed to save task',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      reset();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center">
              <ListTodo className="h-6 w-6 text-brand-teal" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-[1.05rem] font-semibold">
                {editingTask ? 'Edit Task' : 'Create New Task'}
              </DialogTitle>
              <DialogDescription className="text-[0.8rem] text-slate-500 mt-0">
                {editingTask ? 'Update task details' : 'Add a new follow-up task'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 overflow-y-auto">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-medium">
              Task Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="e.g., Follow up on GLP-1 titration"
              className={errors.title ? 'border-red-500' : ''}
            />
            {errors.title && (
              <p className="text-sm text-red-600">{errors.title.message}</p>
            )}
          </div>

          {/* Patient ID (hidden if pre-filled) */}
          {!patientId && !editingTask && (
            <div className="space-y-2">
              <Label htmlFor="patient_id" className="text-sm font-medium">
                Patient <span className="text-red-500">*</span>
              </Label>
              <TaskPatientSelect
                onValueChange={(patient) => {
                  setValue('patient_id', patient.id, { shouldValidate: true });
                }}
                placeholder="Search patients by name or ID"
                hasError={Boolean(errors.patient_id)}
              />
              {errors.patient_id && (
                <p className="text-sm text-red-600">{errors.patient_id.message}</p>
              )}
            </div>
          )}

          {/* Due Date */}
          <div className="space-y-2">
            <Label htmlFor="due_date" className="text-sm font-medium">
              Due Date <span className="text-red-500">*</span>
            </Label>
            <DatePicker
              value={toDateObject(watch('due_date'))}
              onValueChange={(date) => {
                setValue('due_date', date ? format(date, 'MM/dd/yyyy') : '', { shouldValidate: true });
              }}
              placeholder="MM/DD/YYYY"
              inputClassName={errors.due_date ? 'border-red-500' : ''}
            />
            {errors.due_date && (
              <p className="text-sm text-red-600">{errors.due_date.message}</p>
            )}
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label htmlFor="priority" className="text-sm font-medium">
              Priority
            </Label>
            <Select
              value={watch('priority')}
              onValueChange={(value) => setValue('priority', value as any)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category" className="text-sm font-medium">
              Category <span className="text-red-500">*</span>
            </Label>
            <Select
              value={watch('category')}
              onValueChange={(value) => setValue('category', value as any)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="follow_up_visit">Follow-up Visit</SelectItem>
                <SelectItem value="lab_review">Lab Review</SelectItem>
                <SelectItem value="medication_check">Medication Check</SelectItem>
                <SelectItem value="patient_checkin">Patient Check-in</SelectItem>
                <SelectItem value="documentation">Documentation</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-medium">
              Notes (Optional)
            </Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Additional context or instructions..."
              rows={3}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} className="gap-2">
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {editingTask ? 'Update Task' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
